/**
 * Notification utilities and helpers
 */

import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '@lib/firebase';

/**
 * Send booking confirmation notifications (email, SMS, push)
 */
export async function sendBookingConfirmationNotifications(data: {
  userId: string;
  userEmail: string;
  userPhone?: string;
  bookingId: string;
  bookingData: {
    bikeName: string;
    startDate: string;
    endDate: string;
    locationName: string;
    totalPrice: number;
  };
}): Promise<void> {
  const promises: Promise<unknown>[] = [];

  // Send email
  const sendEmail = httpsCallable(functionsUsEast1, 'sendBookingConfirmationEmail');
  promises.push(
    sendEmail({
      bookingId: data.bookingId,
      userEmail: data.userEmail,
      bookingData: data.bookingData,
    }).catch((error) => {
      console.error('Failed to send booking confirmation email:', error);
    })
  );

  // Send push notification
  const sendPush = httpsCallable(functionsUsEast1, 'sendPushToUser');
  promises.push(
    sendPush({
      userId: data.userId,
      title: 'Booking Confirmed!',
      body: `Your e-bike rental for ${data.bookingData.bikeName} is confirmed.`,
      data: {
        type: 'booking_confirmation',
        bookingId: data.bookingId,
      },
      clickAction: `/app/bookings/${data.bookingId}`,
    }).catch((error) => {
      console.error('Failed to send booking confirmation push:', error);
    })
  );

  // Send SMS if phone number provided
  if (data.userPhone) {
    const sendSMS = httpsCallable(functionsUsEast1, 'sendSMS');
    promises.push(
      sendSMS({
        to: data.userPhone,
        message: `Booking confirmed! Your e-bike rental starts ${data.bookingData.startDate} at ${data.bookingData.locationName}. Booking ID: ${data.bookingId}`,
      }).catch((error) => {
        console.error('Failed to send booking confirmation SMS:', error);
      })
    );
  }

  await Promise.allSettled(promises);
}

/**
 * Schedule booking reminder notifications
 */
export async function scheduleBookingReminder(data: {
  userId: string;
  userPhone?: string;
  bookingId: string;
  bookingData: {
    startDate: string;
    locationName: string;
  };
  reminderTime: Date; // When to send reminder (e.g., 24 hours before)
}): Promise<void> {
  // This would typically be handled by Cloud Scheduler
  // For now, we'll just send immediately if reminder time is in the past
  if (data.reminderTime <= new Date()) {
    const promises: Promise<unknown>[] = [];

    // Send push notification
    const sendPush = httpsCallable(functionsUsEast1, 'sendBookingReminderPush');
    promises.push(
      sendPush({
        userId: data.userId,
        bookingData: {
          bookingId: data.bookingId,
          startDate: data.bookingData.startDate,
          locationName: data.bookingData.locationName,
        },
      }).catch((error) => {
        console.error('Failed to send booking reminder push:', error);
      })
    );

    // Send SMS if phone number provided
    if (data.userPhone) {
      const sendSMS = httpsCallable(functionsUsEast1, 'sendBookingReminderSMS');
      promises.push(
        sendSMS({
          phoneNumber: data.userPhone,
          bookingData: {
            bookingId: data.bookingId,
            startDate: data.bookingData.startDate,
            locationName: data.bookingData.locationName,
          },
        }).catch((error) => {
          console.error('Failed to send booking reminder SMS:', error);
        })
      );
    }

    await Promise.allSettled(promises);
  }
}

/**
 * Send return reminder notifications
 */
export async function sendReturnReminder(data: {
  userId: string;
  userPhone?: string;
  bookingId: string;
  bookingData: {
    endDate: string;
    locationName: string;
  };
}): Promise<void> {
  const promises: Promise<unknown>[] = [];

  // Send push notification
  const sendPush = httpsCallable(functionsUsEast1, 'sendPushToUser');
  promises.push(
    sendPush({
      userId: data.userId,
      title: 'Return Reminder',
      body: `Please return your bike by ${data.bookingData.endDate} to ${data.bookingData.locationName}`,
      data: {
        type: 'return_reminder',
        bookingId: data.bookingId,
      },
      clickAction: `/app/bookings/${data.bookingId}`,
    }).catch((error) => {
      console.error('Failed to send return reminder push:', error);
    })
  );

  // Send SMS if phone number provided
  if (data.userPhone) {
    const sendSMS = httpsCallable(functionsUsEast1, 'sendReturnReminderSMS');
    promises.push(
      sendSMS({
        phoneNumber: data.userPhone,
        bookingData: data.bookingData,
      }).catch((error) => {
        console.error('Failed to send return reminder SMS:', error);
      })
    );
  }

  await Promise.allSettled(promises);
}

